import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, GitMerge, AlertTriangle, Check, Search, X, RefreshCw, Sparkles, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useDeduplicationStore } from '@/stores/deduplicationStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DuplicateGroup {
  primarySkill: string;
  duplicates: string[];
  reason: string;
}

interface Skill {
  id: string;
  name: string;
  type: string | null;
  parent_id: string | null;
}

interface Bucket {
  id: string;
  name: string;
}

interface GroupSelection {
  primaryName: string;
  selectedNames: Set<string>;
  targetBucketId: string | null;
}

export const SkillDeduplicationPanel = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [skillCount, setSkillCount] = useState<number>(0);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState<number | null>(null);
  const [groupSelections, setGroupSelections] = useState<Record<number, GroupSelection>>({});
  const { toast } = useToast();
  
  // Use persistent store
  const { 
    skillDuplicates: duplicateGroups, 
    skillScanning: scanning,
    skillLastScanned,
    setSkillDuplicates: setDuplicateGroups,
    setSkillScanning: setScanning
  } = useDeduplicationStore();

  useEffect(() => {
    fetchSkills();
    fetchBuckets();
  }, []);

  // Initialize/sync selections when duplicateGroups change
  useEffect(() => {
    if (duplicateGroups.length > 0) {
      const initialSelections: Record<number, GroupSelection> = {};
      duplicateGroups.forEach((g, idx) => {
        const allNames = [g.primarySkill, ...g.duplicates];
        initialSelections[idx] = {
          primaryName: g.primarySkill,
          selectedNames: new Set(allNames),
          targetBucketId: null,
        };
      });
      setGroupSelections(initialSelections);
    } else {
      setGroupSelections({});
    }
  }, [duplicateGroups]);

  const fetchBuckets = async () => {
    try {
      const { data } = await supabase
        .from('skills')
        .select('id, name')
        .eq('is_bucket', true)
        .order('name');
      setBuckets(data || []);
    } catch (error) {
      console.error('Error fetching buckets:', error);
    }
  };

  const fetchSkills = async () => {
    setLoading(true);
    try {
      // Fetch actual count (bypassing default 1000 limit)
      const { count } = await supabase
        .from('skills')
        .select('*', { count: 'exact', head: true });
      setSkillCount(count || 0);

      const { data, error } = await supabase
        .from('skills')
        .select('id, name, type, parent_id')
        .order('name');

      if (error) throw error;
      setSkills(data || []);
    } catch (error) {
      console.error('Error fetching skills:', error);
    } finally {
      setLoading(false);
    }
  };

  const findSkillByName = (name: string): Skill | undefined => {
    const normalizedName = name.toLowerCase().trim();

    let skill = skills.find(s => s.name.toLowerCase().trim() === normalizedName);
    if (skill) return skill;

    skill = skills.find(s =>
      s.name.toLowerCase().trim().includes(normalizedName) ||
      normalizedName.includes(s.name.toLowerCase().trim())
    );
    if (skill) return skill;

    const cleanName = normalizedName.replace(/[^a-z0-9]/g, '');
    skill = skills.find(s =>
      s.name.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanName
    );

    return skill;
  };

  const validateDuplicateGroups = (groups: DuplicateGroup[]): DuplicateGroup[] => {
    return groups.map(group => {
      const allNames = [group.primarySkill, ...group.duplicates];
      const validNames = allNames.filter(name => findSkillByName(name));

      if (validNames.length < 2) return null;

      return {
        ...group,
        primarySkill: validNames[0],
        duplicates: validNames.slice(1)
      };
    }).filter(Boolean) as DuplicateGroup[];
  };

  const autoMergeExactDuplicates = async (): Promise<number> => {
    // Find exact name duplicates (case-insensitive)
    const nameMap = new Map<string, Skill[]>();
    skills.forEach(skill => {
      const normalizedName = skill.name.toLowerCase().trim();
      if (!nameMap.has(normalizedName)) {
        nameMap.set(normalizedName, []);
      }
      nameMap.get(normalizedName)!.push(skill);
    });

    let mergedCount = 0;
    for (const [, duplicates] of nameMap) {
      if (duplicates.length > 1) {
        // Keep the first one as primary, merge others
        const primary = duplicates[0];
        const toMerge = duplicates.slice(1);

        for (const dupSkill of toMerge) {
          try {
            // Update references
            await supabase
              .from('candidate_skills')
              .update({ skill_id: primary.id })
              .eq('skill_id', dupSkill.id);

            await supabase
              .from('job_skills')
              .update({ skill_id: primary.id })
              .eq('skill_id', dupSkill.id);

            // Log the merge
            await supabase.from('skill_merge_events').insert({
              primary_skill_id: primary.id,
              merged_skill_id: dupSkill.id,
              merged_skill_name: dupSkill.name,
              notes: 'Auto-merged exact duplicate'
            });

            // Delete the duplicate
            await supabase.from('skills').delete().eq('id', dupSkill.id);
            mergedCount++;
          } catch (err) {
            console.error(`Error auto-merging skill ${dupSkill.name}:`, err);
          }
        }
      }
    }

    return mergedCount;
  };

  const detectDuplicates = async () => {
    setScanning(true);
    setError(null);
    setDuplicateGroups([]);
    setGroupSelections({});

    try {
      // First, auto-merge exact duplicates
      const autoMerged = await autoMergeExactDuplicates();
      if (autoMerged > 0) {
        toast({
          title: `Auto-merged ${autoMerged} exact duplicate(s)`,
          description: 'Exact name matches were automatically merged.'
        });
        await fetchSkills(); // Refresh skills list after auto-merge
      }

      // Then run AI-based fuzzy matching for similar but not exact matches
      const { data, error: fnError } = await supabase.functions.invoke('suggest-skills', {
        body: {
          existingSkills: skills.map(s => s.name),
          action: 'find_duplicates'
        }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const groups = data?.duplicateGroups || [];
      const validGroups = validateDuplicateGroups(groups);
      setDuplicateGroups(validGroups);

      // Initialize selections: all selected by default, first one as primary
      const initialSelections: Record<number, GroupSelection> = {};
      validGroups.forEach((g, idx) => {
        const allNames = [g.primarySkill, ...g.duplicates];
        initialSelections[idx] = {
          primaryName: g.primarySkill,
          selectedNames: new Set(allNames),
          targetBucketId: null,
        };
      });
      setGroupSelections(initialSelections);

      if (validGroups.length === 0 && groups.length > 0) {
        toast({
          title: 'Some suggestions could not be matched',
          description: 'AI found potential duplicates but skill names did not match exactly.'
        });
      } else if (validGroups.length === 0 && autoMerged === 0) {
        toast({ title: 'No duplicates detected' });
      } else if (validGroups.length > 0) {
        toast({
          title: `Found ${validGroups.length} potential duplicate group(s)`,
          description: 'Review and merge as needed.'
        });
      }
    } catch (err: any) {
      console.error('Error detecting duplicates:', err);
      setError(err.message || 'Failed to detect duplicates');
    } finally {
      setScanning(false);
    }
  };

  const toggleSkillSelection = (groupIndex: number, skillName: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedNames);

      if (newSelected.has(skillName)) {
        if (skillName === current.primaryName) {
          toast({ title: 'Cannot deselect primary skill', variant: 'destructive' });
          return prev;
        }
        newSelected.delete(skillName);
      } else {
        newSelected.add(skillName);
      }

      return {
        ...prev,
        [groupIndex]: { ...current, selectedNames: newSelected }
      };
    });
  };

  const setPrimarySkill = (groupIndex: number, skillName: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedNames);
      newSelected.add(skillName);

      return {
        ...prev,
        [groupIndex]: { ...current, primaryName: skillName, selectedNames: newSelected }
      };
    });
  };

  const setTargetBucket = (groupIndex: number, bucketId: string | null) => {
    setGroupSelections(prev => ({
      ...prev,
      [groupIndex]: { ...prev[groupIndex], targetBucketId: bucketId }
    }));
  };

  const handleMerge = async (groupIndex: number) => {
    const selection = groupSelections[groupIndex];
    const selectedCount = selection.selectedNames.size;

    if (selectedCount < 2) {
      toast({ title: 'Select at least 2 skills to merge', variant: 'destructive' });
      return;
    }

    const primarySkill = findSkillByName(selection.primaryName);
    const duplicatesToMerge = [...selection.selectedNames]
      .filter(name => name !== selection.primaryName)
      .map(name => findSkillByName(name))
      .filter((s): s is Skill => s !== undefined && s.id !== primarySkill?.id);

    if (!primarySkill) {
      toast({
        title: 'Could not find primary skill',
        description: `"${selection.primaryName}" was not found in the skill list.`,
        variant: 'destructive'
      });
      return;
    }

    if (duplicatesToMerge.length === 0) {
      toast({
        title: 'No duplicates to merge',
        description: 'The duplicate skills could not be found.',
        variant: 'destructive'
      });
      return;
    }

    setMerging(groupIndex);

    try {
      // If a target bucket is selected, update the primary skill's parent
      if (selection.targetBucketId) {
        await supabase
          .from('skills')
          .update({ parent_id: selection.targetBucketId })
          .eq('id', primarySkill.id);
      }

      for (const dupSkill of duplicatesToMerge) {
        await supabase
          .from('candidate_skills')
          .update({ skill_id: primarySkill.id })
          .eq('skill_id', dupSkill.id);

        await supabase
          .from('job_skills')
          .update({ skill_id: primarySkill.id })
          .eq('skill_id', dupSkill.id);

        await supabase.from('skill_merge_events').insert({
          primary_skill_id: primarySkill.id,
          merged_skill_id: dupSkill.id,
          merged_skill_name: dupSkill.name,
          notes: selection.targetBucketId 
            ? `Merged via Data Quality dashboard and assigned to bucket` 
            : 'Merged via Data Quality dashboard'
        });

        await supabase.from('skills').delete().eq('id', dupSkill.id);
      }

      setDuplicateGroups(duplicateGroups.filter((_, idx) => idx !== groupIndex));
      setGroupSelections(prev => {
        const updated = { ...prev };
        delete updated[groupIndex];
        return updated;
      });

      toast({
        title: 'Skills merged successfully',
        description: `Merged ${duplicatesToMerge.length} skill(s) into "${primarySkill.name}".`
      });

      fetchSkills();
    } catch (err) {
      console.error('Error merging skills:', err);
      toast({
        title: 'Failed to merge skills',
        variant: 'destructive'
      });
    } finally {
      setMerging(null);
    }
  };

  const dismissGroup = async (groupIndex: number) => {
    const group = duplicateGroups[groupIndex];
    const primarySkill = findSkillByName(group.primarySkill);
    
    if (primarySkill) {
      // Persist ignored pairs to database
      const ignoreRecords = group.duplicates.map(dupName => {
        const dupSkill = findSkillByName(dupName);
        return dupSkill ? {
          entity_type: 'skill',
          primary_entity_id: primarySkill.id,
          duplicate_entity_id: dupSkill.id,
          status: 'ignored',
          match_score: 80,
          reason: 'Dismissed by user',
        } : null;
      }).filter(Boolean);

      try {
        if (ignoreRecords.length > 0) {
          await supabase.from('duplicate_suggestions').insert(ignoreRecords);
        }
      } catch (error) {
        console.error('Error saving ignored suggestion:', error);
      }
    }

    setDuplicateGroups(duplicateGroups.filter((_, idx) => idx !== groupIndex));
    setGroupSelections(prev => {
      const updated = { ...prev };
      delete updated[groupIndex];
      return updated;
    });
    toast({ title: 'Dismissed and will not appear again' });
  };

  const getMatchedSkillName = (name: string): string => {
    const skill = findSkillByName(name);
    return skill?.name || name;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading skills...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-500" />
            Skill Deduplication
          </h2>
          <p className="text-sm text-muted-foreground">
            Use AI to detect and merge duplicate or similar skills
            {skillLastScanned && (
              <span className="ml-2 text-xs">
                • Last scanned: {new Date(skillLastScanned).toLocaleString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{skillCount} skills</Badge>
          <Button
            onClick={detectDuplicates}
            disabled={scanning}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {scanning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : duplicateGroups.length > 0 ? (
              <RefreshCw className="h-4 w-4" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            {scanning ? 'Scanning...' : duplicateGroups.length > 0 ? 'Rescan' : 'Scan for Duplicates'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <p className="text-sm">{error}</p>
          </CardContent>
        </Card>
      )}

      {duplicateGroups.length === 0 && !scanning && !error && (
        <Card className="border-muted">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="rounded-full bg-muted p-4">
              <Sparkles className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold">AI-Powered Duplicate Detection</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Click "Scan for Duplicates" to analyze your skill list for potential duplicates,
                similar terms, and variations using AI.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {duplicateGroups.length > 0 && (
        <div className="space-y-4">
          {duplicateGroups.map((group, idx) => {
            const selection = groupSelections[idx];
            if (!selection) return null;

            const allNames = [group.primarySkill, ...group.duplicates];
            const isMerging = merging === idx;
            const selectedCount = selection.selectedNames.size;

            return (
              <Card key={idx} className="overflow-hidden">
                <CardHeader className="bg-amber-500/10 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-amber-500/20 p-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      </div>
                      <div>
                        <CardTitle className="text-base">Potential Duplicates</CardTitle>
                        <CardDescription>
                          {selectedCount} of {allNames.length} selected • {group.reason}
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => dismissGroup(idx)}
                      disabled={isMerging}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Select which skills to merge and choose the primary:
                  </p>

                  <RadioGroup
                    value={selection.primaryName}
                    onValueChange={(v) => setPrimarySkill(idx, v)}
                    className="space-y-2"
                    disabled={isMerging}
                  >
                    {allNames.map((name) => {
                      const matchedName = getMatchedSkillName(name);
                      const skill = findSkillByName(name);
                      const isSelected = selection.selectedNames.has(name);
                      const isPrimary = name === selection.primaryName;

                      return (
                        <div
                          key={name}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                            isPrimary
                              ? 'bg-primary/5 border-primary/30 ring-2 ring-primary/20'
                              : isSelected
                                ? 'bg-muted/30 border-border'
                                : 'border-border/50 bg-muted/10 opacity-60'
                          } ${!skill ? 'opacity-50' : ''}`}
                        >
                          <Checkbox
                            id={`check-${idx}-${name}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleSkillSelection(idx, name)}
                            disabled={isPrimary || !skill || isMerging}
                          />
                          <RadioGroupItem
                            value={name}
                            id={`${idx}-${name}`}
                            disabled={!skill || !isSelected}
                          />
                          <Label
                            htmlFor={`${idx}-${name}`}
                            className={`flex items-center gap-2 cursor-pointer flex-1 ${!skill ? 'line-through' : ''}`}
                          >
                            {matchedName}
                            {isPrimary && (
                              <Badge variant="default" className="text-xs">Primary</Badge>
                            )}
                            {skill?.type && (
                              <Badge variant="outline" className="text-xs capitalize">{skill.type}</Badge>
                            )}
                            {!skill && (
                              <Badge variant="destructive" className="text-xs">Not found</Badge>
                            )}
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>

                  {/* Bucket selector */}
                  <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                    <Label className="text-sm whitespace-nowrap">Assign to bucket:</Label>
                    <Select
                      value={selection.targetBucketId || 'none'}
                      onValueChange={(v) => setTargetBucket(idx, v === 'none' ? null : v)}
                      disabled={isMerging}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Keep current bucket" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Keep current bucket</SelectItem>
                        {buckets.map(bucket => (
                          <SelectItem key={bucket.id} value={bucket.id}>
                            {bucket.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={() => handleMerge(idx)}
                    className="w-full gap-2"
                    disabled={isMerging || !findSkillByName(selection.primaryName) || selectedCount < 2}
                  >
                    {isMerging ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <GitMerge className="h-4 w-4" />
                    )}
                    Merge {selectedCount - 1} Selected into "{getMatchedSkillName(selection.primaryName)}"
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
